import React from 'react';
import { TemplateButton, TemplateCard, HeaderType, TemplateLocation } from '@/services/templates.service';
import { Phone, Image as ImageIcon, FileText, MapPin, Reply, ExternalLink, Copy, Play } from 'lucide-react';

interface Props {
  headerType?: HeaderType;
  headerText?: string;
  headerImageUrl?: string;
  headerVideoUrl?: string;
  headerDocumentUrl?: string;
  headerDocumentFilename?: string;
  headerLocation?: TemplateLocation;
  body: string;
  footer?: string;
  buttons: TemplateButton[];
  /** Optional carousel cards — when present, the preview renders horizontal cards under the body. */
  cards?: TemplateCard[];
  samples: Record<string, string>;
  contactName?: string;
}

/** Convert literal "\n" / "\r\n" / "\t" escape sequences (sometimes baked into
 *  AI-generated bodies stored as JSON-escaped strings) into real whitespace. */
function unescapeWhitespace(text: string): string {
  if (!text) return text;
  return text.replace(/\\r\\n/g, '\n').replace(/\\n/g, '\n').replace(/\\t/g, '  ');
}

function substitute(text: string, samples: Record<string, string>): string {
  return unescapeWhitespace(text).replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_m, name: string) =>
    (samples[name] || samples[`parameter_${name}`]) || `{{${name}}}`,
  );
}

function renderRich(text: string): React.ReactNode {
  if (!text) return null;
  return text.split(/\n/).map((line, idx) => (
    <React.Fragment key={idx}>
      {idx > 0 && <br />}
      {parseInline(line)}
    </React.Fragment>
  ));
}

function parseInline(line: string): React.ReactNode {
  const re = /(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|```[^`\n]+```)/g;
  return line.split(re).map((p, i) => {
    if (!p) return null;
    if (p.startsWith('*') && p.endsWith('*')) return <strong key={i}>{p.slice(1, -1)}</strong>;
    if (p.startsWith('_') && p.endsWith('_')) return <em key={i}>{p.slice(1, -1)}</em>;
    if (p.startsWith('~') && p.endsWith('~')) return <s key={i}>{p.slice(1, -1)}</s>;
    if (p.startsWith('```') && p.endsWith('```')) return <code key={i} className="font-mono text-[12px]">{p.slice(3, -3)}</code>;
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

const timeNow = () => new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const WA_GREEN = '#00a884';
const WALLPAPER =
  "#e5ddd5 url(\"data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iYSIgcGF0dGVybnVuaXRzPSJ1c2VyU3BhY2VPblVzZSIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIj48cGF0aCBkPSJNMCAzOGgzOHYySDJ6TTM4IDJoMnYzNkgwVjB6IiBmaWxsPSIjZDlkMGM3IiBmaWxsLW9wYWNpdHk9Ii4zIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2EpIi8+PC9zdmc+\")";

/** A WhatsApp conversation on a phone — the customer's view of the template. */
export const TemplatePreview: React.FC<Props> = (props) => {
  const {
    headerType = 'NONE', headerText, headerImageUrl, headerVideoUrl, headerDocumentUrl, headerDocumentFilename, headerLocation,
    body, footer, buttons, cards, samples, contactName = 'Business Name',
  } = props;
  const isCarousel = Array.isArray(cards) && cards.length > 0;
  const time = timeNow();
  const initial = (contactName.trim()[0] ?? 'B').toUpperCase();

  return (
    <div className="mx-auto w-full max-w-[329px]">
      <div className="relative rounded-[3rem] bg-[#0f172a] p-3 shadow-2xl">
        <div className="absolute left-1/2 top-0 z-10 h-7 w-40 -translate-x-1/2 rounded-b-3xl bg-[#0f172a]" aria-hidden />

        <div className="relative h-[600px] overflow-hidden rounded-[2.5rem]" style={{ background: WALLPAPER }}>
          {/* Header */}
          <div className="flex items-center gap-3 px-4 pb-3 pt-8" style={{ background: WA_GREEN }}>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-base font-semibold text-gray-700">{initial}</div>
            <div className="min-w-0">
              <div className="truncate font-medium text-white">{contactName}</div>
              <div className="text-xs text-white/80">Online</div>
            </div>
          </div>

          {/* Chat */}
          <div className="h-[calc(600px-140px)] overflow-y-auto p-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex justify-start">
              <div className="max-w-[85%] overflow-hidden rounded-md bg-white shadow-[0_1px_0.5px_rgba(0,0,0,0.13)]">
                {headerType === 'TEXT' && headerText && (
                  <div className="border-b border-gray-100 px-3 pb-2 pt-3 text-sm font-semibold text-gray-900">{renderRich(substitute(headerText, samples))}</div>
                )}
                {headerType === 'IMAGE' && (headerImageUrl
                  ? <img src={headerImageUrl} alt="" className="h-40 w-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }} />
                  : <div className="flex h-40 w-full flex-col items-center justify-center gap-2 bg-gray-100 text-gray-400"><ImageIcon size={32} /><span className="text-xs italic">Upload an image</span></div>)}
                {headerType === 'VIDEO' && (headerVideoUrl
                  ? <video src={headerVideoUrl} controls className="h-40 w-full bg-black object-cover" />
                  : <div className="flex h-40 w-full flex-col items-center justify-center gap-2 bg-gray-100 text-gray-400"><span className="flex h-12 w-12 items-center justify-center rounded-full bg-black/30 text-white"><Play size={20} className="fill-white" /></span><span className="text-xs italic">Upload a video</span></div>)}
                {headerType === 'DOCUMENT' && (
                  <a href={headerDocumentUrl || undefined} target="_blank" rel="noreferrer" className="flex items-center gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2">
                    <FileText size={20} className={headerDocumentFilename ? 'text-gray-600' : 'text-gray-400'} />
                    <span className={headerDocumentFilename ? 'truncate text-xs font-medium text-gray-700' : 'text-xs italic text-gray-400'}>{headerDocumentFilename || 'Upload a document'}</span>
                  </a>
                )}
                {headerType === 'LOCATION' && (
                  <div className="border-b border-gray-100 bg-gray-100">
                    <div className="relative h-24 w-full bg-gradient-to-br from-[#a8d5ba] via-[#d4e9df] to-[#e8f4f0]">
                      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'linear-gradient(#cbd5e0 1px, transparent 1px), linear-gradient(90deg, #cbd5e0 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                      <div className="absolute left-0 right-0 top-1/3 h-1 bg-white/60" /><div className="absolute bottom-0 top-0 left-1/3 w-1 bg-white/60" />
                      <MapPin className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[#cc3a36]" size={28} />
                    </div>
                    <div className="flex items-start gap-2 bg-white px-3 py-2">
                      <MapPin size={16} className="mt-0.5 shrink-0" style={{ color: WA_GREEN }} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-gray-900">{headerLocation?.name || 'Pinned location'}</p>
                        <p className="break-words text-xs text-gray-600">{headerLocation?.address || (headerLocation?.latitude && headerLocation?.longitude ? `${headerLocation.latitude}, ${headerLocation.longitude}` : 'Enter location details')}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="whitespace-pre-wrap break-words px-3 py-2.5 text-xs leading-relaxed text-gray-900">
                  {body ? renderRich(substitute(body, samples)) : <span className="text-gray-400">Your message will appear here…</span>}
                </div>

                <div className="flex items-end justify-between gap-2 px-3 pb-2 pt-0.5">
                  {footer ? <p className="whitespace-pre-wrap break-words text-[10px] italic text-gray-500">{footer}</p> : <span />}
                  <span className="shrink-0 text-[9px] text-gray-500">{time}</span>
                </div>

                {!isCarousel && buttons.length > 0 && (
                  <div className="mt-1 border-t border-gray-200">
                    {buttons.map((b, i) => <PreviewButton key={i} button={b} samples={samples} last={i === buttons.length - 1} />)}
                  </div>
                )}
              </div>
            </div>

            {isCarousel && (
              <div className="-mx-4 mt-2 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {cards!.map((card, idx) => (
                  <div key={idx} className="w-[200px] shrink-0 overflow-hidden rounded-md bg-white shadow-[0_1px_0.5px_rgba(0,0,0,0.13)]">
                    {card.headerType === 'VIDEO' && card.headerVideoUrl ? (
                      <video src={card.headerVideoUrl} muted className="block aspect-square w-full object-cover" />
                    ) : card.headerImageUrl ? (
                      <img src={card.headerImageUrl} alt="" className="block aspect-square w-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }} />
                    ) : (
                      <div className="flex aspect-square w-full items-center justify-center bg-gray-100 text-gray-400"><ImageIcon size={28} /></div>
                    )}
                    <div className="whitespace-pre-wrap break-words px-2.5 py-2 text-xs leading-relaxed text-gray-900">
                      {card.body ? renderRich(substitute(card.body, samples)) : <span className="text-gray-400">Card body…</span>}
                    </div>
                    {(card.buttons ?? []).length > 0 && (
                      <div className="border-t border-gray-200">
                        {(card.buttons ?? []).map((b, bi, arr) => <PreviewButton key={bi} button={b} samples={samples} last={bi === arr.length - 1} />)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="absolute inset-x-0 bottom-0 flex items-center gap-2 bg-[#f0f2f5] px-2 py-2">
            <div className="flex-1 rounded-full border border-gray-200 bg-white px-4 py-2"><span className="text-sm text-gray-500">Message</span></div>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full" style={{ background: WA_GREEN }}>
              <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden><path d="M1.101 21.757L23.8 12.028 1.101 2.3l.011 7.912 13.623 1.816-13.623 1.817-.011 7.912z" /></svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PreviewButton: React.FC<{ button: TemplateButton; samples: Record<string, string>; last: boolean }> = ({ button, samples, last }) => {
  let icon: React.ReactNode = null;
  let label = button.text || '(button text)';
  if (button.type === 'URL') icon = <ExternalLink size={14} />;
  if (button.type === 'PHONE') icon = <Phone size={14} />;
  if (button.type === 'QUICK_REPLY') icon = <Reply size={16} />;
  if (button.type === 'COPY_CODE') { icon = <Copy size={14} />; label = button.text || 'Copy code'; void substitute; }
  return (
    <div className={`flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-medium ${last ? '' : 'border-b border-gray-200'}`} style={{ color: WA_GREEN }}>
      {icon}
      <span className="truncate">{label}</span>
    </div>
  );
};
